import { ApolloClient, gql, QueryResult, useApolloClient, useQuery } from '@apollo/client'
import axios from 'axios'
import {
  CreateMembershipInvitationInput,
  MutationCreateMembershipGiftIntentArgs,
  MutationRemoveMembershipSeatArgs,
  CreateMembershipGiftIntentMutation,
  RemoveMembershipSeatMutation,
  DeleteMembershipSeatResponse,
  UpdateMembershipInvitationInput,
  MutationUpdateMembershipGiftIntentArgs,
  UpdateMembershipGiftIntentMutation,
  MembershipSeatsQuery,
  MembershipSeatsQueryVariables,
  PublicMembershipInvitation,
  OpenMembershipInvitationQuery,
  OpenMembershipInvitationQueryVariables,
  AcceptMembershipInvitationResponse,
  MutationAcceptMembershipInvitationArgs,
  UserSubscription,
  UserSubscriptionsQuery,
  UserSubscriptionsQueryVariables,
  FetchUserMembershipQuery,
  FetchMembershipSeatQuery,
  FetchMembershipSeatQueryVariables,
  MembershipGiftIntent,
  MembershipInvitation,
  FetchMembershipSeatForGiftIntentQuery,
  FetchMembershipSeatForGiftIntentQueryVariables,
  CreateEmptyMembershipGiftIntentMutation,
  CreateMembershipInvitationMutation,
  MutationCreateMembershipInvitationArgs,
  UpdateMembershipInvitationMutation,
  MutationUpdateMembershipInvitationArgs,
  FetchAppendableSubscriptionQuery,
  FetchAppendableSubscriptionQueryVariables,
  AddMembershipSeatsMutation,
  AddMembershipSeatsMutationVariables,
  AddSeatsInvoicePreviewQuery,
  AddSeatsInvoicePreviewQueryVariables,
} from '@/types/codegen-federation'
import {
  FetchAllGiftIntentsAndInvitationsQuery,
  FetchAllGiftIntentsAndInvitationsQueryVariables,
} from '@/types/codegen-federation'
import { logger } from '@/utils/logging'
import { Result } from '@/utils/types'
import { getUserSubscriptions } from '../SubscriptionService/SubscriptionService'

export const CREATE_MEMBERSHIP_GIFT_INTENT = gql`
  mutation createMembershipGiftIntent($invitation: CreateMembershipInvitationInput!) {
    createMembershipGiftIntent(invitation: $invitation) {
      id
      state
    }
  }
`
export type MutatedMembershipGiftIntent = NonNullable<CreateMembershipGiftIntentMutation['createMembershipGiftIntent']>

export async function createMembershipGiftIntent(
  client: ApolloClient<object>,
  invitation: CreateMembershipInvitationInput,
): Promise<Result<Pick<MembershipGiftIntent, 'id' | 'state'>>> {
  try {
    const result = await client.mutate<CreateMembershipGiftIntentMutation, MutationCreateMembershipGiftIntentArgs>({
      mutation: CREATE_MEMBERSHIP_GIFT_INTENT,
      variables: { invitation },
      context: { requireAuth: true },
      errorPolicy: 'all',
    })
    return Array.isArray(result.errors) && result.errors.length > 0
      ? { ok: false, error: result.errors[0] }
      : result.data
      ? { ok: true, value: result.data.createMembershipGiftIntent }
      : { ok: false, error: new Error('Create membership gift intent must return the intent') }
  } catch (error) {
    const msg = 'Failed to create membership gift intent'
    logger().error(msg, { invitation }, error)
    return { ok: false, error: error instanceof Error ? error : new Error(msg) }
  }
}

export const CREATE_EMPTY_MEMBERSHIP_GIFT_INTENT = gql`
  mutation createEmptyMembershipGiftIntent {
    createEmptyMembershipGiftIntent {
      id
      state
    }
  }
`

export async function createEmptyMembershipGiftIntent(
  client: ApolloClient<object>,
): Promise<Result<Pick<MembershipGiftIntent, 'id' | 'state'>>> {
  try {
    const result = await client.mutate<CreateEmptyMembershipGiftIntentMutation>({
      mutation: CREATE_EMPTY_MEMBERSHIP_GIFT_INTENT,
      context: { requireAuth: true },
      errorPolicy: 'all',
    })
    return Array.isArray(result.errors) && result.errors.length > 0
      ? { ok: false, error: result.errors[0] }
      : result.data
      ? { ok: true, value: result.data.createEmptyMembershipGiftIntent }
      : { ok: false, error: new Error('Create empty membership gift intent must return the intent') }
  } catch (error) {
    const msg = 'Failed to create empty membership gift intent'
    logger().error(msg, {}, error)
    return { ok: false, error: error instanceof Error ? error : new Error(msg) }
  }
}

export const UPDATE_MEMBERSHIP_GIFT_INTENT = gql`
  mutation updateMembershipGiftIntent($id: ID!, $invitation: UpdateMembershipInvitationInput!) {
    updateMembershipGiftIntent(id: $id, invitation: $invitation) {
      id
      state
    }
  }
`

export async function updateMembershipGiftIntent(
  client: ApolloClient<object>,
  id: string,
  invitation: UpdateMembershipInvitationInput,
): Promise<Result<Pick<MembershipGiftIntent, 'id' | 'state'>>> {
  try {
    const result = await client.mutate<UpdateMembershipGiftIntentMutation, MutationUpdateMembershipGiftIntentArgs>({
      mutation: UPDATE_MEMBERSHIP_GIFT_INTENT,
      variables: { id, invitation },
      context: { requireAuth: true },
      errorPolicy: 'all',
    })
    return Array.isArray(result.errors) && result.errors.length > 0
      ? { ok: false, error: result.errors[0] }
      : result.data
      ? { ok: true, value: result.data.updateMembershipGiftIntent }
      : { ok: false, error: new Error('Update membership gift intent must return the intent') }
  } catch (error) {
    const msg = 'Failed to update membership gift intent'
    logger().error(msg, { invitation }, error)
    return { ok: false, error: error instanceof Error ? error : new Error(msg) }
  }
}

/**
 * Gets all seats that have been purchased by the requesting user.
 */
export const FETCH_ALL_MEMBERSHIP_SEATS = gql`
  query membershipSeats {
    membershipSeats {
      id
      isGift
      productId
      renewalPeriod
      subscriptionStartAt
      tier
      subscriptionId
      renewalPeriod
      subscriptionCancelledAt
      subscriptionPeriodEndAt
      subscriptionStartAt
      assignedTo
      isGift
      productId
      tier
      renewalPeriod
      subscriptionStartAt
      subscriptionPeriodEndAt
      subscriptionCancelledAt
      createdAt
      tier
      updatedAt
      membershipInvitation {
        id
        seatId
        sendAt
        sentAt
        recipientEmail
        recipientName
        recipientMessage
        createdAt
        updatedAt
      }
    }
  }
`
export type MembershipSeat = MembershipSeatsQuery['membershipSeats'][number]

export async function fetchAllMembershipSeats(client: ApolloClient<object>): Promise<Result<MembershipSeat[]>> {
  try {
    const result = await client.query<MembershipSeatsQuery, MembershipSeatsQueryVariables>({
      query: FETCH_ALL_MEMBERSHIP_SEATS,
      context: { requireAuth: true },
      errorPolicy: 'all',
    })
    return Array.isArray(result.errors) && result.errors.length > 0
      ? { ok: false, error: result.errors[0] }
      : { ok: true, value: result.data.membershipSeats }
  } catch (error) {
    const msg = 'Failed to fetch all membership seats'
    logger().error(msg, {}, error)
    return { ok: false, error: error instanceof Error ? error : new Error(msg) }
  }
}
export const FETCH_MEMBERSHIP_SEAT = gql`
  query fetchMembershipSeat($seatId: ID!) {
    membershipSeat(id: $seatId) {
      id
      isGift
      productId
      renewalPeriod
      subscriptionStartAt
      tier
      subscriptionId
      renewalPeriod
      subscriptionCancelledAt
      subscriptionPeriodEndAt
      subscriptionStartAt
      assignedTo
      createdAt
      tier
      updatedAt
      membershipInvitation {
        id
        seatId
        recipientName
        recipientEmail
        recipientMessage
        sendAt
        sentAt
        createdAt
        updatedAt
      }
    }
  }
`

export const REMOVE_MEMBERSHIP_SEAT = gql`
  mutation removeMembershipSeat($seatId: ID!) {
    removeMembershipSeat(seatId: $seatId) {
      id
    }
  }
`

export async function fetchMembershipSeat(
  client: ApolloClient<object>,
  seatId: string,
): Promise<Result<MembershipSeat, MembershipSeatError>> {
  try {
    const result = await client.query<FetchMembershipSeatQuery, FetchMembershipSeatQueryVariables>({
      query: FETCH_MEMBERSHIP_SEAT,
      context: { requireAuth: true },
      errorPolicy: 'all',
      variables: { seatId },
    })

    return Array.isArray(result.errors) && result.errors.length > 0
      ? { ok: false, error: new MembershipSeatError(result.errors[0].message, result.errors[0].extensions?.code) }
      : { ok: true, value: result.data.membershipSeat }
  } catch (error) {
    const msg = 'Failed to fetch membership seat'
    logger().error(msg, { seatId }, error)
    return { ok: false, error: new MembershipSeatError(error instanceof Error ? error.message : msg) }
  }
}

export async function removeMembershipSeat(
  client: ApolloClient<object>,
  seatId: string,
): Promise<Result<DeleteMembershipSeatResponse, MembershipSeatError>> {
  try {
    const result = await client.mutate<RemoveMembershipSeatMutation, MutationRemoveMembershipSeatArgs>({
      mutation: REMOVE_MEMBERSHIP_SEAT,
      variables: { seatId },
      context: { requireAuth: true },
      errorPolicy: 'all',
    })

    if (Array.isArray(result.errors) && result.errors.length > 0) {
      return { ok: false, error: new MembershipSeatError(result.errors[0].message, result.errors[0].extensions?.code) }
    }

    return result.data?.removeMembershipSeat
      ? { ok: true, value: result.data.removeMembershipSeat }
      : { ok: false, error: new MembershipSeatError('Failed to remove membership seat') }
  } catch (error) {
    const msg = 'Failed to remove membership seat'
    logger().error(msg, { seatId }, error)
    return { ok: false, error: new MembershipSeatError(error instanceof Error ? error.message : msg) }
  }
}
export const useFetchAllMembershipSeats = (): QueryResult<MembershipSeatsQuery> => {
  const client = useApolloClient()
  const results = useQuery(FETCH_ALL_MEMBERSHIP_SEATS, {
    client,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    refetchWritePolicy: 'overwrite',
  })
  return results
}

/**
 * Gets the membership seat (and associated invitation) based on the gift intent id (just given)
 */
export const FETCH_MEMBERSHIP_SEAT_FOR_GIFT_INTENT = gql`
  query fetchMembershipSeatForGiftIntent($giftIntentId: ID!) {
    membershipSeat(giftIntentId: $giftIntentId) {
      id
      isGift
      productId
      renewalPeriod
      subscriptionStartAt
      tier
      subscriptionId
      renewalPeriod
      subscriptionCancelledAt
      subscriptionPeriodEndAt
      subscriptionStartAt
      assignedTo
      isGift
      productId
      tier
      renewalPeriod
      subscriptionStartAt
      subscriptionPeriodEndAt
      subscriptionCancelledAt
      createdAt
      tier
      updatedAt
      membershipInvitation {
        id
        seatId
        sendAt
        sentAt
        recipientEmail
        recipientName
        recipientMessage
        createdAt
        updatedAt
      }
    }
  }
`

export async function fetchMembershipSeatForGiftIntent(
  client: ApolloClient<object>,
  giftIntentId: string,
): Promise<Result<MembershipSeat, MembershipSeatError>> {
  try {
    const result = await client.query<
      FetchMembershipSeatForGiftIntentQuery,
      FetchMembershipSeatForGiftIntentQueryVariables
    >({
      query: FETCH_MEMBERSHIP_SEAT_FOR_GIFT_INTENT,
      context: { requireAuth: true },
      errorPolicy: 'all',
      variables: {
        giftIntentId,
      },
    })
    return Array.isArray(result.errors) && result.errors.length > 0
      ? { ok: false, error: new MembershipSeatError(result.errors[0].message, result.errors[0].extensions.code) }
      : { ok: true, value: result.data.membershipSeat }
  } catch (error) {
    const msg = 'Failed to fetch membership seat for gift intent'
    logger().error(msg, { giftIntentId }, error)
    return { ok: false, error: new MembershipSeatError(error instanceof Error ? error.message : msg) }
  }
}

export class MembershipSeatError extends Error {
  public extensions: { code: string }
  constructor(message: string, code?: string) {
    super(message)
    this.extensions = { code: code ?? 'error' }
  }
  get errorCode() {
    return this.extensions.code === 'PENDING' ? 'seat_pending_webhook' : 'error'
  }
}

export const FETCH_ALL_GIFT_INTENTS_AND_INVITATIONS = gql`
  query fetchAllGiftIntentsAndInvitations {
    membershipGiftIntents {
      id
      sendAt
      recipientEmail
      recipientName
      recipientMessage
      state
      createdAt
      updatedAt
    }
    membershipInvitations {
      id
      seatId
      sendAt
      sentAt
      recipientEmail
      recipientName
      recipientMessage
      createdAt
      updatedAt
    }
  }
`

export async function fetchAllGiftIntentsAndInvitations(
  client: ApolloClient<object>,
): Promise<Result<{ intents: MembershipGiftIntent[]; invitations: MembershipInvitation[] }>> {
  try {
    const result = await client.query<
      FetchAllGiftIntentsAndInvitationsQuery,
      FetchAllGiftIntentsAndInvitationsQueryVariables
    >({
      query: FETCH_ALL_GIFT_INTENTS_AND_INVITATIONS,
      context: { requireAuth: true },
      errorPolicy: 'all',
    })
    if (Array.isArray(result.errors) && result.errors.length > 0) {
      return { ok: false, error: result.errors[0] }
    }

    return {
      ok: true,
      value: {
        intents: result.data.membershipGiftIntents,
        invitations: result.data.membershipInvitations,
      },
    }
  } catch (error) {
    const msg = 'Failed to fetch gift intents and invitations'
    logger().error(msg, {}, error)
    return { ok: false, error: error instanceof Error ? error : new Error(msg) }
  }
}

/**
 * Validates a membership invitation by its public invite ID.
 * Returns the invitation with the gifter's userId and the recipientEmail.
 * This query does not require auth.
 */
export const OPEN_MEMBERSHIP_INVITATION = gql`
  query openMembershipInvitation($id: ID!) {
    openMembershipInvitation(id: $id) {
      id
      userId
      giverFirstName
      giverLastName
      recipientEmail
      recipientMessage
      recipientName
      tier
      renewalPeriod
    }
  }
`

export async function openMembershipInvitation(
  client: ApolloClient<object>,
  id: string,
): Promise<Result<PublicMembershipInvitation, MembershipInvitationError>> {
  try {
    const result = await client.query<OpenMembershipInvitationQuery, OpenMembershipInvitationQueryVariables>({
      query: OPEN_MEMBERSHIP_INVITATION,
      variables: { id },
      errorPolicy: 'all',
    })
    if (Array.isArray(result.errors) && result.errors.length > 0) {
      return {
        ok: false,
        error: new MembershipInvitationError(result.errors[0].message, result.errors[0].extensions.code),
      }
    }

    const value = result.data?.openMembershipInvitation
    return value ? { ok: true, value } : { ok: false, error: new MembershipInvitationError('Invitation not found') }
  } catch (error) {
    const msg = 'Failed to fetch open membership invitation'
    logger().error(msg, { id }, error)
    return { ok: false, error: new MembershipInvitationError(error instanceof Error ? error.message : msg) }
  }
}

export class MembershipInvitationError extends Error {
  public extensions: { code: string }
  constructor(message: string, code?: string) {
    super(message)
    this.extensions = { code: code ?? 'BAD_USER_INPUT' }
  }
  get errorCode() {
    return this.extensions.code === 'NOT_FOUND'
      ? 'already_claimed'
      : this.extensions.code === 'FORBIDDEN'
      ? 'already_a_member'
      : 'invalid_code'
  }
}

/**
 * Claims a gifted membership invitation for the authenticated user.
 */
export const ACCEPT_MEMBERSHIP_INVITATION = gql`
  mutation acceptMembershipInvitation($invitationId: ID!) {
    acceptMembershipInvitation(invitationId: $invitationId) {
      ok
    }
  }
`

export async function acceptMembershipInvitation(
  client: ApolloClient<object>,
  invitationId: string,
): Promise<Result<boolean>> {
  try {
    const result = await client.mutate<
      { acceptMembershipInvitation: AcceptMembershipInvitationResponse },
      MutationAcceptMembershipInvitationArgs
    >({
      mutation: ACCEPT_MEMBERSHIP_INVITATION,
      variables: { invitationId },
      context: { requireAuth: true },
      errorPolicy: 'all',
    })
    if (Array.isArray(result.errors) && result.errors.length > 0) {
      return { ok: false, error: result.errors[0] }
    }

    const ok = result.data?.acceptMembershipInvitation?.ok === true
    return ok ? { ok: true, value: true } : { ok: false, error: new Error('Failed to accept invitation') }
  } catch (error) {
    const msg = 'Failed to accept membership invitation'
    logger().error(msg, { invitationId }, error)
    return { ok: false, error: error instanceof Error ? error : new Error(msg) }
  }
}

export async function fetchAllUserSubscriptions(client: ApolloClient<object>): Promise<Result<UserSubscription[]>> {
  try {
    const result = await client.query<UserSubscriptionsQuery, UserSubscriptionsQueryVariables>({
      query: getUserSubscriptions,
      variables: { includeGifts: true },
      context: { requireAuth: true },
      errorPolicy: 'all',
    })
    return Array.isArray(result.errors) && result.errors.length > 0
      ? { ok: false, error: result.errors[0] }
      : { ok: true, value: result.data.user?.subscriptions ?? [] }
  } catch (error) {
    const msg = 'Failed to fetch all user subscriptions'
    logger().error(msg, {}, error)
    return { ok: false, error: error instanceof Error ? error : new Error(msg) }
  }
}

export interface StripeSubscription {
  id: string
  canceled_at: number
}

export async function cancelStripeSubscription(
  authToken: string,
  subscriptionId: string,
): Promise<Result<StripeSubscription>> {
  try {
    const res = await axios.post(
      `${process.env.NEXT_PUBLIC_BFF_API_URL}/stripe/cancel-subscription`,
      {
        subscriptionId,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: authToken,
        },
      },
    )
    return { ok: true, value: res.data }
  } catch (error) {
    const msg = 'Failed to cancel subscription'
    logger().error(msg, { subscriptionId }, error)
    return { ok: false, error: error instanceof Error ? error : new Error(msg) }
  }
}

export type CancelSubscriptionSource = 'STRIPE' | 'RECURLY'

export interface CancelSubscriptionInput {
  email: string
  source: CancelSubscriptionSource
  sourceSubscriptionId: string
}

export interface CancelSubscriptionResponse {
  success: boolean
  source: CancelSubscriptionSource
  subscriptionId?: string
  message: string
  error?: string
}

/**
 * Cancels a subscription via the unified BFF endpoint.
 * Supports both Stripe and Recurly subscriptions.
 * Cancellation is processed immediately but service continues until the end of the current billing period.
 */
export async function cancelSubscriptionViaBff(
  authToken: string,
  input: CancelSubscriptionInput,
): Promise<Result<CancelSubscriptionResponse>> {
  try {
    const res = await axios.post<CancelSubscriptionResponse>(
      `${process.env.NEXT_PUBLIC_BFF_API_URL}/cancel-subscription`,
      input,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: authToken,
        },
      },
    )
    if (!res.data.success) {
      return { ok: false, error: new Error(res.data.error || res.data.message) }
    }
    return { ok: true, value: res.data }
  } catch (error) {
    const msg = 'Failed to cancel subscription via BFF'
    logger().error(msg, { input }, error)
    return { ok: false, error: error instanceof Error ? error : new Error(msg) }
  }
}

export function findSeat(seats: MembershipSeat[], subscription: UserSubscription) {
  return seats.find((seat) => {
    const subscriptionId = formatSubscriptionId(subscription)
    return !!subscriptionId && seat.subscriptionId === subscriptionId
  })
}

function formatSubscriptionId(subscription: UserSubscription) {
  return subscription.sourceId ? `${subscription.source.toLowerCase()}:${subscription.sourceId}` : undefined
}

/**
 * DESIGN NOTE - currently not selecting primaryProduct. That should probably be
 * done as a part of the membership management work, where that's all cleaned up and
 * from this one source. But for now, I'm trying to support only gifted membership with
 * this query. primaryProduct is not guaranteeed to be the gifted one, so iterating
 * the products is required.
 */
export const FETCH_USER_MEMBERSHIP = gql`
  query fetchUserMembership {
    user {
      id
      membership {
        products {
          id
          tier
          startAt
          endAt
          status
          giftAttributes {
            giverFirstName
            giverLastName
            subscriptionOwnerId
          }
        }
      }
      myMembership {
        active
        memberSince
        primaryProduct {
          id
          tier
          startAt
          endAt
          isGift
          subscription {
            cancelledAt
          }
        }
        additionalProducts {
          id
          tier
          startAt
          endAt
          isGift
          subscription {
            cancelledAt
          }
        }
      }
    }
  }
`

/**
 * DESIGN NOTE - using this mirror of Membership instead of the type on the graph, because this is specific to the fields selected
 */
export type Membership = NonNullable<NonNullable<FetchUserMembershipQuery['user']>['myMembership']>

/**
 * DESIGN NOTE - using this mirror of MembershipProduct instead of the type on the graph, because this is specific to the fields selected
 */
export type MembershipProduct = Membership['additionalProducts'][number]

/**
 * Determines if the user has only a gifted membership (no paid membership).
 * Used to determine if a user can remain in the sales funnel to buy a membership. If they have
 * no primary product or only a gifted membership, they *can* still buy.
 */
export function hasOnlyGiftedMembership(membership: Membership | null | undefined): boolean {
  return !membership?.primaryProduct || !!membership.primaryProduct.isGift
}

export const useFetchUserMembership = (): QueryResult<FetchUserMembershipQuery> => {
  const client = useApolloClient()
  const results = useQuery(FETCH_USER_MEMBERSHIP, {
    client,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    refetchWritePolicy: 'overwrite',
  })
  return results
}

export const CREATE_MEMBERSHIP_INVITATION = gql`
  mutation createMembershipInvitation($seatId: ID!, $invitation: CreateMembershipInvitationInput!) {
    createMembershipInvitation(seatId: $seatId, invitation: $invitation) {
      id
      seatId
      sendAt
      sentAt
      recipientEmail
      recipientName
      recipientMessage
      createdAt
      updatedAt
    }
  }
`
export type MutatedMembershipInvitation = CreateMembershipInvitationMutation['createMembershipInvitation']

export async function createMembershipInvitation(
  client: ApolloClient<object>,
  seatId: string,
  invitation: CreateMembershipInvitationInput,
): Promise<Result<MutatedMembershipInvitation, MembershipInvitationError>> {
  try {
    const result = await client.mutate<CreateMembershipInvitationMutation, MutationCreateMembershipInvitationArgs>({
      mutation: CREATE_MEMBERSHIP_INVITATION,
      variables: { seatId, invitation },
      context: { requireAuth: true },
      errorPolicy: 'all',
    })
    return Array.isArray(result.errors) && result.errors.length > 0
      ? { ok: false, error: new MembershipInvitationError(result.errors[0].message, result.errors[0].extensions.code) }
      : result.data
      ? { ok: true, value: result.data.createMembershipInvitation }
      : { ok: false, error: new MembershipInvitationError('Create membership invitation must return the invitation') }
  } catch (error) {
    const msg = 'Failed to create membership invitation'
    logger().error(msg, { invitation }, error)
    return { ok: false, error: new MembershipInvitationError(error instanceof Error ? error.message : msg) }
  }
}

export const UPDATE_MEMBERSHIP_INVITATION = gql`
  mutation updateMembershipInvitation($id: ID!, $invitation: UpdateMembershipInvitationInput!) {
    updateMembershipInvitation(id: $id, invitation: $invitation) {
      id
      seatId
      sendAt
      sentAt
      recipientEmail
      recipientName
      recipientMessage
      createdAt
      updatedAt
    }
  }
`

export async function updateMembershipInvitation(
  client: ApolloClient<object>,
  invitationId: string,
  invitation: UpdateMembershipInvitationInput,
): Promise<Result<MutatedMembershipInvitation, MembershipInvitationError>> {
  try {
    const result = await client.mutate<UpdateMembershipInvitationMutation, MutationUpdateMembershipInvitationArgs>({
      mutation: UPDATE_MEMBERSHIP_INVITATION,
      variables: { id: invitationId, invitation },
      context: { requireAuth: true },
      errorPolicy: 'all',
    })
    return Array.isArray(result.errors) && result.errors.length > 0
      ? { ok: false, error: new MembershipInvitationError(result.errors[0].message, result.errors[0].extensions.code) }
      : result.data
      ? { ok: true, value: result.data.updateMembershipInvitation }
      : { ok: false, error: new MembershipInvitationError('Update membership invitation must return the invitation') }
  } catch (error) {
    const msg = 'Failed to update membership invitation'
    logger().error(msg, { invitation }, error)
    return { ok: false, error: new MembershipInvitationError(error instanceof Error ? error.message : msg) }
  }
}

export const ADD_MEMBERSHIP_SEAT = gql`
  mutation addMembershipSeats($subscriptionId: ID!, $productId: ID!, $quantity: Int!) {
    addMembershipSeats(subscriptionId: $subscriptionId, productId: $productId, quantity: $quantity) {
      id
      subscriptionId
      assignedTo
      isGift
      productId
      tier
      renewalPeriod
      subscriptionStartAt
      subscriptionPeriodEndAt
      subscriptionCancelledAt
      createdAt
      updatedAt
      membershipInvitation {
        id
        seatId
        sendAt
        sentAt
        recipientEmail
        recipientName
        recipientMessage
        createdAt
        updatedAt
      }
    }
  }
`

export async function addMembershipSeats(
  client: ApolloClient<object>,
  /**
   * Stripe vendor subscription id
   */
  subscriptionId: string,
  /**
   * Which product (eg, basic with ads or basic) that's being attached to the seat.
   */
  productId: string,
  quantity: number,
): Promise<Result<MembershipSeat[], MembershipSeatError>> {
  try {
    const result = await client.mutate<AddMembershipSeatsMutation, AddMembershipSeatsMutationVariables>({
      mutation: ADD_MEMBERSHIP_SEAT,
      variables: { subscriptionId, productId, quantity },
      context: { requireAuth: true },
      errorPolicy: 'all',
    })
    return Array.isArray(result.errors) && result.errors.length > 0
      ? { ok: false, error: new MembershipSeatError(result.errors[0].message, result.errors[0].extensions.code) }
      : result.data
      ? { ok: true, value: result.data.addMembershipSeats }
      : { ok: false, error: new MembershipSeatError('Save membership seats must return the added seats') }
  } catch (error) {
    const msg = 'Failed to add membership seats'
    logger().error(msg, { subscriptionId, productId, quantity }, error)
    return { ok: false, error: new MembershipSeatError(error instanceof Error ? error.message : msg) }
  }
}

/*
 * Gets the subscription that products can be added to. The algorithm to select which, of my
 * potentially many subscriptions, is returned is a black box, owned on the BE.
 */
export const FETCH_APPENDABLE_SUBSCRIPTION = gql`
  query fetchAppendableSubscription {
    appendableSubscription {
      id
      tier
      totalPrice
      currency
    }
  }
`
export type AppendableSubscription = NonNullable<FetchAppendableSubscriptionQuery['appendableSubscription']>

export async function fetchAppendableSubscription(
  client: ApolloClient<object>,
): Promise<Result<AppendableSubscription | undefined>> {
  try {
    const result = await client.query<FetchAppendableSubscriptionQuery, FetchAppendableSubscriptionQueryVariables>({
      query: FETCH_APPENDABLE_SUBSCRIPTION,
      context: { requireAuth: true },
      errorPolicy: 'all',
    })
    return Array.isArray(result.errors) && result.errors.length > 0
      ? { ok: false, error: result.errors[0] }
      : { ok: true, value: result.data.appendableSubscription ?? undefined }
  } catch (error) {
    const msg = 'Failed to fetch appendable subscription'
    logger().error(msg, {}, error)
    return { ok: false, error: error instanceof Error ? error : new Error(msg) }
  }
}

export const ADD_SEATS_INVOICE_PREVIEW = gql`
  query addSeatsInvoicePreview($subscriptionId: ID!, $productId: ID!, $quantity: Int!) {
    addSeatsInvoicePreview(subscriptionId: $subscriptionId, productId: $productId, quantity: $quantity) {
      currency
      subtotal
      total
      lines {
        amount
        description
        quantity
      }
    }
  }
`

export type AddSeatsInvoicePreview = AddSeatsInvoicePreviewQuery['addSeatsInvoicePreview']

export function useAddSeatsInvoicePreview(
  subscriptionId: string,
  productId: string,
  quantity: number,
): QueryResult<AddSeatsInvoicePreviewQuery, AddSeatsInvoicePreviewQueryVariables> {
  return useQuery<AddSeatsInvoicePreviewQuery, AddSeatsInvoicePreviewQueryVariables>(ADD_SEATS_INVOICE_PREVIEW, {
    variables: { subscriptionId, productId, quantity },
    context: { requireAuth: true },
    errorPolicy: 'all',
  })
}
